import { fromApiPayload, toApiPayload } from './serialize';
import type { Drawing } from './types';
import { buildTenantStorageKey } from '../../lib/storage/tenantStorage';
import { readPrivateTradingJson, writePrivateTradingJson } from '../../lib/storage/privateTradingStorage';
import { getCurrentSession } from '../../lib/auth.ts';
import { applyTenantRequestHeaders } from '../../lib/tenantRequestContext';

const LOCAL_STORAGE_DOMAIN = 'chart-drawings';
const LOCAL_STORAGE_KEY = buildTenantStorageKey('tt_chart_drawings_v1');
const LOCAL_STORAGE_KEY_LEGACY = 'tt_chart_drawings_v1';

function buildBaseCandidates(): string[] {
  let envBase = '';
  try {
    envBase = import.meta.env?.VITE_API_BASE || '';
  } catch {}
  const clean = String(envBase || '').replace(/\/+$/, '');
  return Array.from(new Set([clean, '']));
}

async function fetchJson(url: string, init?: RequestInit) {
  const session = await getCurrentSession();
  const accessToken = String(session?.access_token ?? '').trim();
  if (!accessToken) throw new Error('missing_access_token');
  const headers: Record<string, string> = Object.fromEntries(Object.entries((init?.headers || {}) as Record<string, string>));
  headers.Authorization = `Bearer ${accessToken}`;
  applyTenantRequestHeaders(headers);
  const res = await fetch(url, { ...init, headers });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(payload?.error || payload?.message || `HTTP ${res.status}`);
  }
  return payload;
}

function canUseLocalStorage(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
  } catch {
    return false;
  }
}

function readLocalDrawings(): Drawing[] {
  if (!canUseLocalStorage()) return [];
  try {
    const raw = readPrivateTradingJson(LOCAL_STORAGE_DOMAIN, 'local', [LOCAL_STORAGE_KEY, LOCAL_STORAGE_KEY_LEGACY]);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(fromApiPayload);
  } catch {
    return [];
  }
}

function writeLocalDrawings(drawings: Drawing[]): void {
  if (!canUseLocalStorage()) return;
  try {
    writePrivateTradingJson(LOCAL_STORAGE_DOMAIN, JSON.stringify(drawings.map(toApiPayload)));
  } catch {
    // ignore storage write failures
  }
}

function getLocalScopedDrawings(symbol: string, tradeId?: string): { symbolDrawings: Drawing[]; tradeDrawings: Drawing[] } {
  const targetSymbol = String(symbol || '').toUpperCase();
  const targetTradeId = tradeId ? String(tradeId) : null;
  const all = readLocalDrawings();

  const symbolDrawings = all.filter((d) => d.scope === 'symbol' && String(d.symbol || '').toUpperCase() === targetSymbol);
  const tradeDrawings = all.filter((d) => {
    if (d.scope !== 'trade') return false;
    if (String(d.symbol || '').toUpperCase() !== targetSymbol) return false;
    if (!targetTradeId) return false;
    return String(d.tradeId || '') === targetTradeId;
  });

  return { symbolDrawings, tradeDrawings };
}

function upsertLocalDrawing(drawing: Drawing): Drawing {
  const all = readLocalDrawings();
  const next = [...all];
  const idx = next.findIndex((d) => d.id === drawing.id);
  if (idx >= 0) next[idx] = drawing;
  else next.push(drawing);
  writeLocalDrawings(next);
  return drawing;
}

function deleteLocalDrawingById(id: string): void {
  const all = readLocalDrawings();
  const next = all.filter((d) => d.id !== id);
  writeLocalDrawings(next);
}

export async function getDrawings(symbol: string, tradeId?: string): Promise<{ symbolDrawings: Drawing[]; tradeDrawings: Drawing[] }> {
  const sym = encodeURIComponent(String(symbol || '').toUpperCase());
  const trade = tradeId ? `&tradeId=${encodeURIComponent(String(tradeId))}` : '';

  for (const base of buildBaseCandidates()) {
    const url = `${base}/api/drawings?symbol=${sym}${trade}`;
    try {
      const payload = await fetchJson(url);
      return {
        symbolDrawings: Array.isArray(payload?.symbolDrawings) ? payload.symbolDrawings.map(fromApiPayload) : [],
        tradeDrawings: Array.isArray(payload?.tradeDrawings) ? payload.tradeDrawings.map(fromApiPayload) : [],
      };
    } catch {}
  }

  // Fallback: local persistence keeps tools usable when API/DB is unavailable.
  return getLocalScopedDrawings(symbol, tradeId);
}

export async function upsertDrawing(drawing: Drawing): Promise<Drawing> {
  const payload = JSON.stringify(toApiPayload(drawing));

  for (const base of buildBaseCandidates()) {
    const url = `${base}/api/drawings`;
    try {
      const data = await fetchJson(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
      });
      return fromApiPayload(data?.drawing || data);
    } catch {}
  }

  // Fallback: save locally to avoid breaking drawing UX during backend outages.
  return upsertLocalDrawing(drawing);
}

export async function deleteDrawing(id: string): Promise<void> {
  for (const base of buildBaseCandidates()) {
    const url = `${base}/api/drawings/${encodeURIComponent(String(id))}`;
    try {
      await fetchJson(url, { method: 'DELETE' });
      return;
    } catch {}
  }

  // Fallback: delete locally when API is unreachable.
  deleteLocalDrawingById(id);
}
